export const translations = {
  en: {
    nav: {
      legacy: "Legacy",
      science: "Science",
      land: "The Land",
      process: "Process",
      blends: "Blends",
      standard: "The Standard",
      work: "Work With Us",
      archive: "Archive",
      heritage: "Heritage",
      craft: "The Craft",
      collection: "The Collection",
      who_we_are: "Who We Are",
      seed_to_smoke: "Seed to Smoke",
      master_blender: "Master Blender",
      final_cigar: "The Final Cigar",
      work_with_us: "Work With Us",
    },
    journey: {
      eyebrow: "Chapter III — From Seed to Smoke",
      title: "From seed to smoke.",
      intro:
        "Every Ramírez cigar is the work of nine careful seasons — from the first seed planted in southern Dominican soil to the final aroma that meets the smoker. Each step builds the one that follows.",
      pull:
        "“Nothing is hurried. Each leaf earns the next stage.”",
      steps: [
        {
          n: "01",
          k: "The Seed",
          t: "Where it begins.",
          d: "We hand-select the seed each year — varieties chosen for the southern climate, the rainfall, and the character we want decades from now.",
        },
        {
          n: "02",
          k: "Cultivation",
          t: "Patience in the field.",
          d: "Plants grow in long, disciplined rows under the Cordillera Central. Sun, mountain breeze and clay-loam soil shape the leaf for ten weeks.",
        },
        {
          n: "03",
          k: "Harvest",
          t: "Leaf by leaf, by hand.",
          d: "Workers pick the plant in primings — bottom to top, over several visits — so each leaf is taken at its true maturity. Nothing is rushed.",
        },
        {
          n: "04",
          k: "Curing",
          t: "Air, shade, time.",
          d: "Leaves are hung inside wooden curing barns for 42 days. Doors are opened and closed daily to control humidity, color and aroma.",
        },
        {
          n: "05",
          k: "Fermentation",
          t: "The leaf becomes itself.",
          d: "Stacked in pilones for 90 days, the tobacco breathes and breaks down its harshness. Temperature is read by hand — never by machine.",
        },
        {
          n: "06",
          k: "Aging",
          t: "Quiet years.",
          d: "Bales rest three to seven years in our cedar warehouse. The leaf softens, balances, and develops the depth that defines a Ramírez cigar.",
        },
        {
          n: "07",
          k: "Blending",
          t: "The blender's hand.",
          d: "Our master blender selects wrapper, binder and filler — leaf by leaf — until the smoke speaks in one voice. This is where the cigar's identity is made.",
        },
        {
          n: "08",
          k: "Rolling",
          t: "Built by hand.",
          d: "Each cigar is rolled and capped on a wooden table by trained hands. Pressure, fold and feel are tuned for a clean, even draw.",
        },
        {
          n: "09",
          k: "The Final Smoke",
          t: "Time, returned.",
          d: "When you light a Ramírez cigar, you taste nine seasons — the southern earth, the slow curing, the years of rest. The patience returns to you as aroma.",
        },
      ],
      bridge_left: "From the leaf, a voice is born. The Master Blender gives it shape.",
      bridge_right: "Chapter IV · Master Blender",
    },
    master_blender: {
      eyebrow: "Chapter IV — Master Blender",
      title: "Three leaves. One voice.",
      intro:
        "A Ramírez cigar is built from three families of tobacco — wrapper, binder and filler. The master blender chooses each leaf for a specific reason, and combines them so the smoke reads as one continuous flavor, not three.",
      pull: "“The blend is the cigar's signature. Everything else is preparation.”",
      bridge_left:
        "The blend is set. Now the leaves take their final shape.",
      bridge_right: "Chapter V · The Final Cigar",
    },
    hero: {
      eyebrow: "Est. 1948 — San Juan de la Maguana, Dominican Republic",
      title_a: "A standard",
      title_b: "earned.",
      subtitle: "Dominican tobacco. Time. Discipline.",
      description:
        "From the fertile lands of the Dominican Republic to the future of premium tobacco craftsmanship.",
      stat_1: "78 years of heritage",
      stat_2: "1,800+ acres in production",
      stat_3: "468+ workers daily",
      cta_primary: "Explore the Legacy",
      cta_secondary: "Enter the Archive",
      scroll: "Scroll",
    },
    legacy: {
      eyebrow: "Chapter I — Who We Are",
      title: "A family that helped shape the tobacco industry.",
      body_1:
        "We are the Ramírez family, three generations of Dominican tobacco growers from the south — San Juan de la Maguana, Las Matas de Farfán, and Elías Piña. Our first hectare was planted in 1948. Today our family still leads the field, the curing barns, and the blending table — the same way our grandfather did.",
      body_2:
        "We do not chase trends. We refine, repeat, and respect the leaf. Every Ramírez cigar is the result of more than seventy years of discipline — a quiet conversation between soil, climate, hand, and time.",
      caption_1: "Eliodoro Ramírez — Founder",
      caption_2: "Las Matas de Farfán, 1956",
      pull: "Honor is grown slowly, like a tobacco leaf.",
    },
    science: {
      eyebrow: "Chapter III — The Craft / Anatomy",
      title: "An anatomy of a great cigar.",
      intro: "Hover any term to study its role in the Ramirez blend.",
      parts: {
        wrapper: {
          name: "Wrapper",
          mono: "01 / Capa",
          desc: "The outermost leaf. Sets visual identity and ~60% of the flavor.",
          notes: "Cedar, cocoa, leather",
          strength: "Medium",
        },
        binder: {
          name: "Binder",
          mono: "02 / Capote",
          desc: "Holds the filler in tension. Quietly shapes the burn.",
          notes: "Earth, dry hay",
          strength: "Medium",
        },
        filler: {
          name: "Filler",
          mono: "03 / Tripa",
          desc: "The blended core. Three leaves, three altitudes, one voice.",
          notes: "Spice, espresso, almond",
          strength: "Full",
        },
        ligero: {
          name: "Ligero",
          mono: "04 / Crown leaf",
          desc: "Top-priming leaf. Potency, oils, slow combustion.",
          notes: "Black pepper, cedar",
          strength: "Full",
        },
        seco: {
          name: "Seco",
          mono: "05 / Mid leaf",
          desc: "Middle priming. Aroma and structural balance.",
          notes: "Toast, cocoa",
          strength: "Medium",
        },
        volado: {
          name: "Volado",
          mono: "06 / Foot leaf",
          desc: "Bottom priming. Combustion engine of the blend.",
          notes: "Hay, cream",
          strength: "Mild",
        },
        fermentation: {
          name: "Fermentation",
          mono: "07 / Pilón",
          desc: "Stacked leaves under their own weight. Slow chemistry.",
          notes: "90 days minimum",
          strength: "—",
        },
        aging: {
          name: "Aging",
          mono: "08 / Añejamiento",
          desc: "Cedar-bound rest. Roundness emerges.",
          notes: "12–60 months",
          strength: "—",
        },
      },
    },
    land: {
      eyebrow: "Chapter II — The Land",
      title: "1,800 acres of patient earth.",
      body_1:
        "Our fields lie in the San Juan Valley — Las Matas de Farfán, Elías Piña — at the foot of the Sierra de Neiba. Long dry seasons, mountain air at night, and clay-loam soil give our tobacco a quiet sweetness and a balance that nothing else can copy.",
      body_2:
        "The southern climate is honest with the leaf: hot days that build sugar, cool nights that hold aroma, and a rainy season that arrives on time. We read each parcel by hand — soil, water, leaf, sun — every season.",
      stat_a: "1,800+",
      stat_a_label: "Acres in active production",
      stat_b: "468+",
      stat_b_label: "Workers in the daily craft",
      stat_c: "78",
      stat_c_label: "Years of unbroken stewardship",
    },
    process: {
      eyebrow: "Chapter III — The Craft / Process",
      title: "Ten quiet stages, seven seasons of patience.",
      steps: [
        { n: "01", k: "Seed Selection", d: "Curated cultivars from a private seed bank." },
        { n: "02", k: "Cultivation", d: "Hand-tilled rows, balanced canopy, daily reading." },
        { n: "03", k: "Harvest", d: "Priming by priming, leaf by leaf." },
        { n: "04", k: "Drying", d: "Open-air curing barns, six weeks of stillness." },
        { n: "05", k: "Fermentation", d: "Pilones rebuilt every seven days for ninety." },
        { n: "06", k: "Aging", d: "Cedar bales, 12 to 60 months of repose." },
        { n: "07", k: "Blending", d: "Master blender layers ligero, seco, volado." },
        { n: "08", k: "Rolling", d: "Hand-rolled by torcedor, by feel, by tension." },
        { n: "09", k: "Packaging", d: "Cedar-lined boxes, archival labels." },
        { n: "10", k: "Final Experience", d: "Read like a manuscript — slowly." },
      ],
    },
    blends: {
      eyebrow: "Chapter IV — The Collection",
      title: "A library of carefully balanced voices.",
      items: [
        {
          name: "Ramirez Reserva",
          year: "Vintage 2017",
          origin: "San Juan",
          wrapper: "Habano Rosado",
          strength: "Medium–Full",
          notes: "Cedar · Cocoa · Espresso · Black Pepper",
          intensity: 4,
        },
        {
          name: "Patrimonio Verde",
          year: "Vintage 2019",
          origin: "Villa González",
          wrapper: "Connecticut Shade",
          strength: "Mild–Medium",
          notes: "Almond · Cream · Toast · Hay",
          intensity: 2,
        },
        {
          name: "Manuscrito Negro",
          year: "Vintage 2015",
          origin: "Tamboril",
          wrapper: "Maduro Oscuro",
          strength: "Full",
          notes: "Dark Chocolate · Leather · Dried Fig · Earth",
          intensity: 5,
        },
        {
          name: "Hermanos Edición",
          year: "Vintage 2020",
          origin: "Mao",
          wrapper: "Corojo Criollo",
          strength: "Medium",
          notes: "Cedar · Honey · Walnut · White Pepper",
          intensity: 3,
        },
      ],
    },
    standard: {
      eyebrow: "Chapter I — Heritage / Standard",
      title: "Crafted with patience.",
      pillars: [
        { k: "Discipline", d: "Every leaf measured. Every priming recorded." },
        { k: "Heritage", d: "Three generations. One method." },
        { k: "Craftsmanship", d: "The hand. Still the truest instrument." },
        { k: "Trust", d: "We protect the brands we work with as our own." },
      ],
      pull: "Built from generations of Dominican tobacco.",
    },
    work: {
      eyebrow: "Chapter VII — Work With Us",
      title: "Two paths into a Ramírez partnership.",
      intro:
        "We support partners in two carefully separated disciplines. Tell us where you are, and we will meet you with the right team.",
      path_a_title: "Raw Material",
      path_a_list: [
        "Tobacco supply",
        "Leaf preparation",
        "Curing",
        "Fermentation",
        "Master blender preparation",
      ],
      path_b_title: "Private Label · Full Brand Development",
      path_b_list: [
        "Custom blend development",
        "Cigar engineering",
        "Packaging & cigar bands",
        "Boxes & finishing",
        "Branding, marketing & web",
        "End-to-end production",
      ],
      qualifier_title: "Briefly qualify your project",
      q1: "Do you already have a Master Blender?",
      q2: "Do you need custom development?",
      q3: "Are you building a new cigar brand?",
      yes: "Yes",
      no: "No",
      form_title: "Begin a quiet conversation",
      name: "Name",
      email: "Email",
      company: "Company",
      country: "Country",
      message: "Tell us about your project",
      submit: "Submit Brief",
      submitting: "Sending…",
      success: "Thank you. The Ramirez office will be in touch.",
      error: "Something went wrong. Please try again.",
      path: "Path",
      path_raw: "Raw Material",
      path_private: "Private Label",
    },
    archive: {
      eyebrow: "Chapter V — The Archive",
      title: "A private library of tobacco knowledge.",
      intro:
        "Selected manuscripts on anatomy, fermentation, Dominican culture and the small ceremonies that surround a great cigar.",
      read: "Read entry",
      back: "Back to Archive",
      empty: "Archive is being curated.",
    },
    footer: {
      brand: "Eliasramirez · The Ramirez Brothers",
      addr: "Las Matas de Farfán · Elías Piña",
      rights: "All rights reserved.",
      admin: "Admin",
    },
    cellar: {
      label: "Maestro’s Cellar",
      title: "A private invitation.",
      body: "Receive new manuscript entries, seasonal tobacco notes and quiet archive releases — four times a year.",
      placeholder: "Your email address",
      button: "Subscribe",
      sending: "Sending…",
      success: "Thank you. The Maestro will write to you soon.",
      error: "Could not subscribe. Please try again.",
    },
    loading: {
      label: "The Ramirez Brothers",
      sub: "A Dominican Tobacco Manuscript",
    },
    login: {
      title: "Archive · Administration",
      sub: "Restricted entry. Authorized stewards only.",
      email: "Email",
      password: "Password",
      submit: "Enter",
      submitting: "Verifying…",
      error: "Invalid credentials.",
      back: "Return to website",
    },
    admin: {
      dashboard: "Dashboard",
      submissions: "Submissions",
      archive: "Archive",
      logout: "Logout",
      view_site: "View site",
      no_submissions: "No submissions yet.",
      delete: "Delete",
      new_entry: "New Entry",
      title_en: "Title (EN)",
      title_es: "Title (ES)",
      excerpt_en: "Excerpt (EN)",
      excerpt_es: "Excerpt (ES)",
      body_en: "Body (EN)",
      body_es: "Body (ES)",
      category: "Category",
      cover: "Cover Image URL",
      published: "Published",
      save: "Save",
      cancel: "Cancel",
      edit: "Edit",
      confirm_delete: "Delete this entry?",
      welcome: "Welcome back, steward.",
      total_submissions: "Total submissions",
      total_archive: "Archive entries",
      latest: "Latest leads",
    },
  },
  es: {
    nav: {
      legacy: "Legado",
      science: "Ciencia",
      land: "La Tierra",
      process: "Proceso",
      blends: "Ligas",
      standard: "El Estándar",
      work: "Trabaje con nosotros",
      archive: "Archivo",
      heritage: "Herencia",
      craft: "El Oficio",
      collection: "La Colección",
      who_we_are: "Quiénes Somos",
      seed_to_smoke: "De la Semilla al Humo",
      master_blender: "Maestro Ligador",
      final_cigar: "El Cigarro",
      work_with_us: "Trabaja con Nosotros",
    },
    journey: {
      eyebrow: "Capítulo III — De la Semilla al Humo",
      title: "De la semilla al humo.",
      intro:
        "Cada cigarro Ramírez es el trabajo de nueve estaciones cuidadosas — desde la primera semilla plantada en la tierra del sur dominicano hasta el aroma final que llega al fumador. Cada paso construye el siguiente.",
      pull: "“Nada se apura. Cada hoja se gana la próxima etapa.”",
      steps: [
        {
          n: "01",
          k: "La Semilla",
          t: "Donde todo empieza.",
          d: "Seleccionamos la semilla a mano cada año — variedades elegidas para el clima del sur, la lluvia y el carácter que queremos décadas más adelante.",
        },
        {
          n: "02",
          k: "Cultivo",
          t: "Paciencia en el campo.",
          d: "Las plantas crecen en surcos largos y disciplinados bajo la Cordillera Central. Sol, brisa de montaña y suelo franco arcilloso forman la hoja durante diez semanas.",
        },
        {
          n: "03",
          k: "Cosecha",
          t: "Hoja por hoja, a mano.",
          d: "Los trabajadores recogen la planta en cortes — de abajo hacia arriba, en varias visitas — para que cada hoja se tome en su verdadera madurez. Nada se apura.",
        },
        {
          n: "04",
          k: "Curado",
          t: "Aire, sombra, tiempo.",
          d: "Las hojas se cuelgan dentro de casas de curado de madera durante 42 días. Las puertas se abren y se cierran cada día para controlar humedad, color y aroma.",
        },
        {
          n: "05",
          k: "Fermentación",
          t: "La hoja se vuelve ella misma.",
          d: "Apiladas en pilones durante 90 días, el tabaco respira y suelta su aspereza. La temperatura se lee con la mano — nunca con máquina.",
        },
        {
          n: "06",
          k: "Añejamiento",
          t: "Años tranquilos.",
          d: "Las pacas reposan de tres a siete años en nuestra bodega de cedro. La hoja suaviza, balancea y desarrolla la profundidad que define un cigarro Ramírez.",
        },
        {
          n: "07",
          k: "Ligado",
          t: "La mano del ligador.",
          d: "Nuestro maestro ligador escoge capa, capote y tripa — hoja por hoja — hasta que el humo habla en una sola voz. Aquí nace la identidad del cigarro.",
        },
        {
          n: "08",
          k: "Liado",
          t: "Construido a mano.",
          d: "Cada cigarro se lía y se le coloca la perilla sobre una mesa de madera por manos entrenadas. Presión, doblez y tacto se ajustan para un tiro limpio y parejo.",
        },
        {
          n: "09",
          k: "El Humo Final",
          t: "El tiempo, devuelto.",
          d: "Cuando enciende un Ramírez, está probando nueve estaciones — la tierra del sur, el curado lento, los años de reposo. La paciencia regresa a usted como aroma.",
        },
      ],
      bridge_left: "De la hoja nace una voz. El Maestro Ligador le da forma.",
      bridge_right: "Capítulo IV · Maestro Ligador",
    },
    master_blender: {
      eyebrow: "Capítulo IV — Maestro Ligador",
      title: "Tres hojas. Una sola voz.",
      intro:
        "Un cigarro Ramírez se arma con tres familias de tabaco — capa, capote y tripa. El maestro ligador escoge cada hoja por una razón específica y las combina para que el humo se lea como un solo sabor continuo, no como tres.",
      pull: "“La liga es la firma del cigarro. Todo lo demás es preparación.”",
      bridge_left: "La liga está hecha. Ahora las hojas toman su forma final.",
      bridge_right: "Capítulo V · El Cigarro",
    },
    hero: {
      eyebrow: "Fundado en 1948 — San Juan de la Maguana, República Dominicana",
      title_a: "Un estándar",
      title_b: "ganado.",
      subtitle: "Tabaco dominicano. Tiempo. Disciplina.",
      description:
        "Desde las tierras fértiles de la República Dominicana al futuro de la artesanía del tabaco premium.",
      stat_1: "78 años de herencia",
      stat_2: "1,800+ acres en producción",
      stat_3: "468+ trabajadores diarios",
      cta_primary: "Explorar el Legado",
      cta_secondary: "Entrar al Archivo",
      scroll: "Desplazar",
    },
    legacy: {
      eyebrow: "Capítulo I — Quiénes Somos",
      title: "Una familia que ayudó a dar forma a la industria del tabaco.",
      body_1:
        "Somos la familia Ramírez, tres generaciones de tabacaleros dominicanos del sur — San Juan de la Maguana, Las Matas de Farfán y Elías Piña. Nuestra primera hectárea se sembró en 1948. Hoy nuestra familia sigue al frente del campo, de las casas de curado y de la mesa de ligado — igual que lo hacía nuestro abuelo.",
      body_2:
        "No perseguimos modas. Refinamos, repetimos y respetamos la hoja. Cada cigarro Ramírez es el resultado de más de setenta años de disciplina — una conversación silenciosa entre el suelo, el clima, la mano y el tiempo.",
      caption_1: "Eliodoro Ramírez — Fundador",
      caption_2: "Las Matas de Farfán, 1956",
      pull: "El honor se cultiva despacio, como una hoja de tabaco.",
    },
    science: {
      eyebrow: "Capítulo III — El Oficio / Anatomía",
      title: "Anatomía de un gran cigarro.",
      intro: "Pase el cursor por cada término para estudiar su papel en la liga Ramírez.",
      parts: {
        wrapper: {
          name: "Capa",
          mono: "01 / Capa",
          desc: "La hoja más externa. Define la identidad visual y ~60% del sabor.",
          notes: "Cedro, cacao, cuero",
          strength: "Medio",
        },
        binder: {
          name: "Capote",
          mono: "02 / Capote",
          desc: "Sostiene la tripa en tensión. Da forma silenciosa al tiro.",
          notes: "Tierra, heno seco",
          strength: "Medio",
        },
        filler: {
          name: "Tripa",
          mono: "03 / Tripa",
          desc: "El núcleo de la liga. Tres hojas, tres altitudes, una voz.",
          notes: "Especia, espresso, almendra",
          strength: "Fuerte",
        },
        ligero: {
          name: "Ligero",
          mono: "04 / Hoja corona",
          desc: "Hoja de pilada superior. Potencia, aceites, combustión lenta.",
          notes: "Pimienta negra, cedro",
          strength: "Fuerte",
        },
        seco: {
          name: "Seco",
          mono: "05 / Hoja media",
          desc: "Pilada media. Aroma y equilibrio estructural.",
          notes: "Tostado, cacao",
          strength: "Medio",
        },
        volado: {
          name: "Volado",
          mono: "06 / Hoja base",
          desc: "Pilada inferior. El motor de combustión de la liga.",
          notes: "Heno, crema",
          strength: "Suave",
        },
        fermentation: {
          name: "Fermentación",
          mono: "07 / Pilón",
          desc: "Hojas apiladas bajo su propio peso. Química lenta.",
          notes: "90 días mínimo",
          strength: "—",
        },
        aging: {
          name: "Añejamiento",
          mono: "08 / Reposo",
          desc: "Reposo en cedro. La redondez emerge.",
          notes: "12–60 meses",
          strength: "—",
        },
      },
    },
    land: {
      eyebrow: "Capítulo II — La Tierra",
      title: "1,800 acres de tierra paciente.",
      body_1:
        "Nuestros campos están en el Valle de San Juan — Las Matas de Farfán, Elías Piña — al pie de la Sierra de Neiba. Largas estaciones secas, aire de montaña por la noche y suelo franco arcilloso le dan a nuestro tabaco una dulzura silenciosa y un balance que nadie más puede copiar.",
      body_2:
        "El clima del sur es honesto con la hoja: días calurosos que construyen el azúcar, noches frescas que sostienen el aroma y una temporada de lluvia que llega a tiempo. Leemos cada parcela a mano — suelo, agua, hoja, sol — cada temporada.",
      stat_a: "1,800+",
      stat_a_label: "Acres en producción activa",
      stat_b: "468+",
      stat_b_label: "Trabajadores en la labor diaria",
      stat_c: "78",
      stat_c_label: "Años de custodia ininterrumpida",
    },
    process: {
      eyebrow: "Capítulo III — El Oficio / Proceso",
      title: "Diez etapas silenciosas, siete temporadas de paciencia.",
      steps: [
        { n: "01", k: "Selección de Semilla", d: "Cultivares curados de un banco de semillas privado." },
        { n: "02", k: "Cultivo", d: "Surcos labrados a mano, dosel equilibrado, lectura diaria." },
        { n: "03", k: "Cosecha", d: "Pilada por pilada, hoja por hoja." },
        { n: "04", k: "Secado", d: "Ranchos al aire libre, seis semanas de quietud." },
        { n: "05", k: "Fermentación", d: "Pilones rearmados cada siete días durante noventa." },
        { n: "06", k: "Añejamiento", d: "Pacas de cedro, 12 a 60 meses de reposo." },
        { n: "07", k: "Liga", d: "El maestro liga ligero, seco y volado." },
        { n: "08", k: "Torcido", d: "Hecho a mano por torcedor, al tacto, en tensión." },
        { n: "09", k: "Empaque", d: "Cajas forradas en cedro, etiquetas de archivo." },
        { n: "10", k: "Experiencia Final", d: "Se lee como un manuscrito — despacio." },
      ],
    },
    blends: {
      eyebrow: "Capítulo IV — La Colección",
      title: "Una biblioteca de voces cuidadosamente equilibradas.",
      items: [
        {
          name: "Ramírez Reserva",
          year: "Cosecha 2017",
          origin: "San Juan",
          wrapper: "Habano Rosado",
          strength: "Medio–Fuerte",
          notes: "Cedro · Cacao · Espresso · Pimienta Negra",
          intensity: 4,
        },
        {
          name: "Patrimonio Verde",
          year: "Cosecha 2019",
          origin: "Villa González",
          wrapper: "Connecticut Shade",
          strength: "Suave–Medio",
          notes: "Almendra · Crema · Tostado · Heno",
          intensity: 2,
        },
        {
          name: "Manuscrito Negro",
          year: "Cosecha 2015",
          origin: "Tamboril",
          wrapper: "Maduro Oscuro",
          strength: "Fuerte",
          notes: "Chocolate Negro · Cuero · Higo Seco · Tierra",
          intensity: 5,
        },
        {
          name: "Hermanos Edición",
          year: "Cosecha 2020",
          origin: "Mao",
          wrapper: "Corojo Criollo",
          strength: "Medio",
          notes: "Cedro · Miel · Nuez · Pimienta Blanca",
          intensity: 3,
        },
      ],
    },
    standard: {
      eyebrow: "Capítulo I — Herencia / Estándar",
      title: "Hecho con paciencia.",
      pillars: [
        { k: "Disciplina", d: "Cada hoja medida. Cada pilada registrada." },
        { k: "Herencia", d: "Tres generaciones. Un solo método." },
        { k: "Artesanía", d: "La mano. Aún el instrumento más preciso." },
        { k: "Confianza", d: "Protegemos cada marca como la nuestra." },
      ],
      pull: "Construido por generaciones de tabaco dominicano.",
    },
    work: {
      eyebrow: "Capítulo VII — Trabaja con Nosotros",
      title: "Dos caminos hacia una alianza con Ramírez.",
      intro:
        "Acompañamos a nuestros socios en dos disciplinas cuidadosamente separadas. Díganos dónde se encuentra y le presentaremos al equipo correcto.",
      path_a_title: "Materia Prima",
      path_a_list: [
        "Suministro de tabaco",
        "Preparación de hoja",
        "Curado",
        "Fermentación",
        "Preparación de maestro ligador",
      ],
      path_b_title: "Marca Privada · Desarrollo de Marca",
      path_b_list: [
        "Desarrollo de liga personalizada",
        "Ingeniería del cigarro",
        "Empaque y anillas",
        "Cajas y acabados",
        "Branding, marketing y web",
        "Producción integral",
      ],
      qualifier_title: "Califique brevemente su proyecto",
      q1: "¿Ya cuenta con un Maestro Ligador?",
      q2: "¿Necesita desarrollo personalizado?",
      q3: "¿Está construyendo una nueva marca de cigarros?",
      yes: "Sí",
      no: "No",
      form_title: "Comience una conversación silenciosa",
      name: "Nombre",
      email: "Correo electrónico",
      company: "Compañía",
      country: "País",
      message: "Cuéntenos sobre su proyecto",
      submit: "Enviar Brief",
      submitting: "Enviando…",
      success: "Gracias. La oficina Ramírez se pondrá en contacto.",
      error: "Algo salió mal. Por favor, intente de nuevo.",
      path: "Camino",
      path_raw: "Materia Prima",
      path_private: "Marca Privada",
    },
    archive: {
      eyebrow: "Capítulo V — El Archivo",
      title: "Una biblioteca privada de saber tabacalero.",
      intro:
        "Manuscritos seleccionados sobre anatomía, fermentación, cultura dominicana y las pequeñas ceremonias que rodean un gran cigarro.",
      read: "Leer entrada",
      back: "Volver al Archivo",
      empty: "El archivo está siendo curado.",
    },
    footer: {
      brand: "Eliasramirez · Los Hermanos Ramírez",
      addr: "Las Matas de Farfán · Elías Piña",
      rights: "Todos los derechos reservados.",
      admin: "Administración",
    },
    cellar: {
      label: "Bodega del Maestro",
      title: "Una invitación privada.",
      body: "Reciba nuevas entradas del manuscrito, notas estacionales del tabaco y discretos lanzamientos del archivo — cuatro veces al año.",
      placeholder: "Su correo electrónico",
      button: "Suscribirse",
      sending: "Enviando…",
      success: "Gracias. El Maestro le escribirá pronto.",
      error: "No fue posible suscribirse. Intente de nuevo.",
    },
    loading: {
      label: "Los Hermanos Ramírez",
      sub: "Un Manuscrito de Tabaco Dominicano",
    },
    login: {
      title: "Archivo · Administración",
      sub: "Acceso restringido. Solo custodios autorizados.",
      email: "Correo",
      password: "Contraseña",
      submit: "Entrar",
      submitting: "Verificando…",
      error: "Credenciales inválidas.",
      back: "Volver al sitio",
    },
    admin: {
      dashboard: "Panel",
      submissions: "Solicitudes",
      archive: "Archivo",
      cellar: "Bodega",
      logout: "Salir",
      view_site: "Ver sitio",
      no_submissions: "Aún no hay solicitudes.",
      no_subscribers: "Aún no hay suscriptores.",
      delete: "Eliminar",
      new_entry: "Nueva Entrada",
      title_en: "Título (EN)",
      title_es: "Título (ES)",
      excerpt_en: "Extracto (EN)",
      excerpt_es: "Extracto (ES)",
      body_en: "Cuerpo (EN)",
      body_es: "Cuerpo (ES)",
      category: "Categoría",
      cover: "URL de imagen de portada",
      published: "Publicado",
      save: "Guardar",
      cancel: "Cancelar",
      edit: "Editar",
      confirm_delete: "¿Eliminar esta entrada?",
      welcome: "Bienvenido de nuevo, custodio.",
      total_submissions: "Solicitudes totales",
      total_archive: "Entradas del archivo",
      total_cellar: "Suscriptores de la Bodega",
      latest: "Solicitudes recientes",
    },
  },
};
